import * as React from 'react';
import { styled, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import CssBaseline from '@mui/material/CssBaseline';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import Grid from '@mui/material/Grid';
import MuiLink from '@mui/material/Link';
import { MainListItems, SecondaryListItems } from './ListItems';
import { useNavigation, useOutlet } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';
import { useThemeContext } from 'contexts/themeContext';
import { LOGOUT, PRIVATE_ADMIN } from 'config/router/paths';
import { useAuthContext } from 'contexts/authContext';
import AppLoader from 'components/common/AppLoader';

const drawerWidth = 240;

function Copyright(props) {
  return (
    <Typography variant="body2" color="text.secondary" align="center" {...props}>
      {'Copyright © '}
      <MuiLink color="inherit" href="/">
        {process.env.REACT_APP_SITENAME}
      </MuiLink>{' '}
      {new Date().getFullYear()}
      {'.'}
    </Typography>
  );
}

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: `-${drawerWidth}px`,
    ...(open && {
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
    }),
  }),
);

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  height: '10vh',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));

export default function Dashboard() {
  const theme = useTheme();
  const { toggleColorMode, mode } = useThemeContext();
  const { isAdmin } = useAuthContext();
  const [open, setOpen] = React.useState(false);
  const { state } = useNavigation();

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const outlet = useOutlet();

  const loadOutlet = () => {
    if (state === "loading") {
      return <AppLoader />;
    } else {
      if (outlet) {
        return outlet;
      } else {
        return (
          <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '70vh' }}>
            <img
              src={theme.palette.mode === 'dark'
                ? 'https://eu2.contabostorage.com/68e1ac55689b4b99a9a2ccd407d95b2a:branding-absor/logos/absor/v1/complete_white.svg'
                : 'https://eu2.contabostorage.com/68e1ac55689b4b99a9a2ccd407d95b2a:branding-absor/logos/absor/v1/complete.svg'
              }
              alt="logo"
              style={{ maxWidth: '250px' }}
            />
          </Grid>
        );
      }
    }
  };

  React.useEffect(() => {
    const handleDocumentClick = (event) => {
      if (event.target.closest('.MuiListItemButton-root')) {
        handleDrawerClose();
      }
    };
  
    document.addEventListener('click', handleDocumentClick);
  
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, []);

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="fixed" open={open} sx={{ height: '10vh', bgcolor: 'background.paper', color: 'text.primary' }}>
        <Toolbar sx={{ height: '10vh' }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{ mr: 2, ...(open && { display: 'none' }) }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontWeight: 700 }}>
            {process.env.REACT_APP_SITENAME}
          </Typography>
          {isAdmin() && (
            <Tooltip title="Administración">
              <IconButton component={RouterLink} to={PRIVATE_ADMIN} color="inherit" aria-label="administración">
                <SettingsIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Cerrar sesión">
            <IconButton component={RouterLink} to={LOGOUT} color="inherit" aria-label="cerrar sesión">
              <LogoutIcon />
            </IconButton>
          </Tooltip>
          <IconButton onClick={toggleColorMode} color="inherit" aria-label="toggle color mode">
            {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
        </Toolbar>
      </AppBar>
      <Drawer
        sx={{
          height: '90vh',
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            display: 'flex',
            height: '100%',
          },
        }}
        variant="persistent"
        anchor="left"
        open={open}
      >
        <DrawerHeader>
          <Box sx={{ flexGrow: 1, pl: 1, display: 'flex', alignItems: 'center' }}>
            <img
              src={theme.palette.mode === 'dark'
                ? 'https://eu2.contabostorage.com/68e1ac55689b4b99a9a2ccd407d95b2a:branding-absor/logos/absor/v1/complete_white.svg'
                : 'https://eu2.contabostorage.com/68e1ac55689b4b99a9a2ccd407d95b2a:branding-absor/logos/absor/v1/complete.svg'
              }
              alt="logo"
              style={{ maxHeight: '28px' }}
            />
          </Box>
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === 'ltr' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        </DrawerHeader>
        <Divider />
        <List component="nav" sx={{ pt: 1, pb: 1, flexGrow: 1, overflowY: 'auto' }}>
            <MainListItems />
        </List>
        <Box sx={{ flexShrink: 0, pb: 1 }}>
          <Divider sx={{ my: 1 }} />
          <List component="nav">
            <SecondaryListItems />
          </List>
        </Box>
      </Drawer>
      <Main open={open} sx={{ height: '90vh', padding: 0 }}>
        <DrawerHeader />
        <Box sx={{ margin: 0, padding: "24px", height: '80vh', width: '100vw', overflowY: 'scroll', maxWidth: '100%' }}>
            {loadOutlet()}
        </Box>
        <Copyright sx={{ pt: 4, height: '10vh'}}/>
      </Main>
    </Box>
  );
}
